import { AUTH_LEARNER_CREATED } from "@outschool/auth-shared";
import { ChildFragmentFragment } from "@outschool/gql-frontend-generated";
import { useSession } from "@outschool/ui-auth";
import Cookies from "js-cookie";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { useHistory, useLocation } from "react-router";

import { useIsParentDashboardPage } from "../components/parentDashboardV2/hooks/useDashboardLinks";

const LOCAL_STORAGE_KEY = "activeLearner";
export const ACTIVE_LEARNER_QUERY_PARAM = "activeLearner";

/**
 * Determines how long the active learner selection persists in local storage.
 * - `ONCE`: The selection is used once, then cleared from storage. Useful for
 *   temporary selections like when a user searches in the global nav bar with default "All learners" selected.
 * - `PERPETUAL`: The selection persists until explicitly changed. Used for
 *   intentional learner selections that should be remembered across sessions.
 */
export const ActiveLearnerLifetime = {
  ONE_PAGE_LOAD: "ONE_PAGE_LOAD",
  PERPETUAL: "PERPETUAL",
} as const;
const ActiveLearnerLifetimeValues = Object.values(ActiveLearnerLifetime);
export type ActiveLearnerLifetime =
  (typeof ActiveLearnerLifetime)[keyof typeof ActiveLearnerLifetime];

/**
 * Stored format for the active learner selection in local storage.
 * When no value exists in storage, default logic determines the active learner.
 */
export interface StoredActiveLearner {
  /** The selected learner's UID, or null for "All learners" */
  learnerUid: string | null;
  /** How long this selection should persist */
  lifetime: ActiveLearnerLifetime;
}

/**
 * Input format for setting the active learner. Accepts either:
 * - A StoredActiveLearner object for full control over persistence
 * - A simple string UID (defaults to PERPETUAL lifetime)
 * - null to select "All learners" (defaults to PERPETUAL lifetime)
 */
export type SetActiveLearnerInput = StoredActiveLearner | string | null;

function isStoredActiveLearner(value: unknown): value is StoredActiveLearner {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    (typeof obj.learnerUid === "string" || obj.learnerUid === null) &&
    ActiveLearnerLifetimeValues.includes(obj.lifetime as ActiveLearnerLifetime)
  );
}

function parseStoredValue(raw: string | null): StoredActiveLearner | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    return isStoredActiveLearner(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function readFromLocalStorage(): StoredActiveLearner | null {
  try {
    // eslint-disable-next-line no-restricted-globals
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    return parseStoredValue(raw);
  } catch {
    return null;
  }
}

function writeToLocalStorage(value: StoredActiveLearner | null): void {
  try {
    if (value === null) {
      // eslint-disable-next-line no-restricted-globals
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } else {
      // eslint-disable-next-line no-restricted-globals
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(value));
    }
  } catch {
    // Ignore storage errors (e.g., quota exceeded, private browsing)
  }
}

interface ActiveLearnerContextValue {
  activeLearner: ChildFragmentFragment | null;
  setActiveLearner: (input: SetActiveLearnerInput) => void;
  showLearnerFilter: boolean;
  activeLearnerSet: boolean;
  lastExplicitSelection: {
    learnerUid: string | null;
  } | null;
}

const ActiveLearnerContext = createContext<ActiveLearnerContextValue>({
  activeLearner: null,
  setActiveLearner: () => {},
  showLearnerFilter: false,
  activeLearnerSet: false,
  lastExplicitSelection: null,
});

interface ActiveLearnerProviderProps {
  children: ReactNode;
}

export function ActiveLearnerProvider({
  children,
}: ActiveLearnerProviderProps) {
  const [storedValue, setStoredValue] =
    React.useState<StoredActiveLearner | null>(readFromLocalStorage);
  const lastExplicitSelectionRef = React.useRef<{
    learnerUid: string | null;
  } | null>(null);
  const { currentUser } = useSession();
  const history = useHistory();
  const location = useLocation();
  const requestedLearnerUid = React.useMemo(() => {
    const learnerUid = new URLSearchParams(location.search)
      .get(ACTIVE_LEARNER_QUERY_PARAM)
      ?.trim();
    return learnerUid || null;
  }, [location.search]);

  const showLearnerFilter = Boolean(currentUser?.children?.length);

  const isParentDashboardPage = useIsParentDashboardPage();

  // Compute the active learner, applying default logic when no stored value exists
  const activeLearner = React.useMemo(() => {
    if (storedValue === null) {
      // No stored value: apply default logic
      if (isParentDashboardPage) {
        return null;
      }
      return currentUser?.children.at(-1) ?? null;
    }

    // Handle ONCE lifetime: clear storage after reading the value
    if (storedValue?.lifetime === "ONE_PAGE_LOAD") {
      writeToLocalStorage(null);
    }

    if (storedValue.learnerUid === null) {
      // Explicitly selected "All learners"
      return null;
    }

    return (
      currentUser?.children.find(
        child => child?.uid === storedValue.learnerUid,
      ) ?? null
    );
  }, [currentUser, storedValue, isParentDashboardPage]);

  const setActiveLearner = useCallback((input: SetActiveLearnerInput) => {
    let newValue: StoredActiveLearner;

    if (isStoredActiveLearner(input)) {
      newValue = input;
    } else {
      // Simple string or null: default to PERPETUAL lifetime
      newValue = {
        learnerUid: input,
        lifetime: "PERPETUAL",
      };
    }

    lastExplicitSelectionRef.current = {
      learnerUid: newValue.learnerUid,
    };
    setStoredValue(newValue);
    writeToLocalStorage(newValue);
  }, []);

  // Deep links (for example, from email) cannot call setActiveLearner before
  // navigation, so apply a valid learner selection from the URL after the
  // session has loaded.
  useEffect(() => {
    const requestedLearnerExists = currentUser?.children.some(
      child => child.uid === requestedLearnerUid,
    );
    if (!requestedLearnerExists) {
      return;
    }

    if (storedValue?.learnerUid !== requestedLearnerUid) {
      setActiveLearner(requestedLearnerUid);
    }

    const queryParams = new URLSearchParams(location.search);
    queryParams.delete(ACTIVE_LEARNER_QUERY_PARAM);
    const search = queryParams.toString();
    history.replace({
      pathname: location.pathname,
      search: search ? `?${search}` : "",
      hash: location.hash,
    });
  }, [
    currentUser?.children,
    history,
    location.hash,
    location.pathname,
    location.search,
    requestedLearnerUid,
    setActiveLearner,
    storedValue?.learnerUid,
  ]);

  // If a learner was just created during signup/onboarding, set the active learner
  useEffect(() => {
    const isLearnerCreated = Cookies.get(AUTH_LEARNER_CREATED);
    if (isLearnerCreated) {
      const createdLearner = currentUser?.children.at(0);
      if (createdLearner) {
        setActiveLearner(createdLearner.uid);
      }
      Cookies.remove(AUTH_LEARNER_CREATED);
    }
  }, [currentUser?.children, setActiveLearner]);

  return (
    <ActiveLearnerContext.Provider
      value={{
        activeLearner,
        setActiveLearner,
        showLearnerFilter,
        activeLearnerSet: storedValue !== null,
        lastExplicitSelection: lastExplicitSelectionRef.current,
      }}
    >
      {children}
    </ActiveLearnerContext.Provider>
  );
}

export interface ActiveLearnerChangeDetails {
  isExplicitSelection: boolean;
}

interface UseActiveLearnerArgs {
  onActiveLearnerChanged?: (
    activeLearner: ChildFragmentFragment | null,
    changeDetails: ActiveLearnerChangeDetails,
  ) => void;
}

export function useActiveLearner(opts: UseActiveLearnerArgs = {}) {
  const context = useContext(ActiveLearnerContext);
  const onActiveLearnerChangedRef = React.useRef(opts.onActiveLearnerChanged);
  const initializedRef = React.useRef(false);
  const previousActiveLearnerRef = React.useRef(context.activeLearner);
  const handledExplicitSelectionRef = React.useRef(
    context.lastExplicitSelection,
  );

  if (context === undefined) {
    throw new Error(
      "useActiveLearner must be used within an ActiveLearnerProvider",
    );
  }

  // Keep ref up-to-date
  useEffect(() => {
    onActiveLearnerChangedRef.current = opts.onActiveLearnerChanged;
  }, [opts.onActiveLearnerChanged]);

  // Call the callback when activeLearner changes (skip initial mount)
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      previousActiveLearnerRef.current = context.activeLearner;
      handledExplicitSelectionRef.current = context.lastExplicitSelection;
      return;
    }

    const previousActiveLearner = previousActiveLearnerRef.current;
    const activeLearnerChanged =
      previousActiveLearner !== context.activeLearner;
    const explicitSelection = context.lastExplicitSelection;
    const explicitSelectionMatchesActiveLearner =
      explicitSelection !== null &&
      explicitSelection !== handledExplicitSelectionRef.current &&
      explicitSelection.learnerUid === (context.activeLearner?.uid ?? null);

    previousActiveLearnerRef.current = context.activeLearner;
    if (explicitSelectionMatchesActiveLearner) {
      handledExplicitSelectionRef.current = explicitSelection;
    }

    if (!activeLearnerChanged) {
      return;
    }

    if (onActiveLearnerChangedRef.current) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
      onActiveLearnerChangedRef.current(context.activeLearner, {
        isExplicitSelection:
          explicitSelectionMatchesActiveLearner &&
          previousActiveLearner?.uid !== context.activeLearner?.uid,
      });
    }
  }, [context.activeLearner, context.lastExplicitSelection]);

  const {
    lastExplicitSelection: _lastExplicitSelection,
    ...activeLearnerContext
  } = context;
  return activeLearnerContext;
}
